/**
 * Download / CSV export actions for the claims dashboard.
 *
 * Split from claimsDashboardActionCreator.js for maintainability.
 */
import { toastr } from 'react-redux-toastr';
import { makeApiCall } from '../../../../../services/apiService/apiService';
import { downloadCSV, createCSV } from '../../../../../services/helpers';
import {
  claimRoleTypes,
  getClaimRole,
  getColumnsForRole
} from '../claimsDashboardHelper';
import { getClientVisibleStatus } from '../../claimsUtils';
import { buildClaimFilters } from './claimFilterBuilder';
import { setClaimsDataAction } from './claimsDashboardActionCreator';

const formatDateValue = val => {
  if (!val) return '-';
  if (typeof val === 'string') return val;
  const d = new Date(val);
  if (isNaN(d.getTime())) return val;
  const options = { day: '2-digit', month: 'short', year: 'numeric' };
  return d.toLocaleDateString('en-GB', options).replace(/,/g, '');
};

export const getCSVColumnsForRole = (role, reasonList) => {
  if (role === claimRoleTypes.HQ_DEPS) {
    return [
      {
        Header: 'CN',
        formatter: data => data.cnote || data.cnNumber || data.cn || ''
      },
      {
        Header: 'Client Code',
        formatter: data =>
          data.clientCode || (data.rawData && data.rawData.clientCode) || ''
      },
      {
        Header: 'Organisation Code',
        formatter: data =>
          data.orgCode ||
          (data.rawData && data.rawData.orgCode) ||
          data.orgId ||
          ''
      },
      {
        Header: 'Reason',
        formatter: data => {
          const reasonId = data.reason || data.reasonId;
          const foundReason = reasonList.find(r => r && r.id === reasonId);
          return foundReason ? foundReason.reason : reasonId || '-';
        }
      },
      {
        Header: 'Status',
        formatter: data => {
          const status = data.status || '-';
          return status === '-' ? '-' : status.replace(/_/g, ' ').toUpperCase();
        }
      },
      {
        Header: 'Request amount',
        formatter: data =>
          data.requestedAmount !== null && data.requestedAmount !== undefined
            ? data.requestedAmount
            : '-'
      },
      {
        Header: 'Approved amount',
        formatter: data =>
          data.approvedAmount !== null && data.approvedAmount !== undefined
            ? data.approvedAmount
            : '-'
      },
      {
        Header: 'Claim Action',
        formatter: data => data.claimAction || '-'
      },
      {
        Header: 'Recoverable',
        formatter: data => data.recoverable || '-'
      },
      {
        Header: 'Request raised Date',
        formatter: data =>
          formatDateValue(data.raisedDate || data.requestedDate)
      },
      {
        Header: 'RCA Submitted Date',
        formatter: data => formatDateValue(data.rcaSubmittedDate)
      },
      {
        Header: 'Responsible Person + OU',
        formatter: data => {
          let responsiblePartiesVal = data.responsibleParties;
          if (!responsiblePartiesVal && data.rawData) {
            responsiblePartiesVal = data.rawData.responsibleParties;
          }
          if (!responsiblePartiesVal) return '-';
          try {
            const parsed =
              typeof responsiblePartiesVal === 'string'
                ? JSON.parse(responsiblePartiesVal)
                : responsiblePartiesVal;
            if (Array.isArray(parsed) && parsed.length > 0) {
              return parsed
                .map(item =>
                  [item.personName || item.personEmail, item.ouCode]
                    .filter(Boolean)
                    .join(' - ')
                )
                .join(', ');
            }
            return String(responsiblePartiesVal);
          } catch (e) {
            return String(responsiblePartiesVal);
          }
        }
      },
      {
        Header: 'RCA Approved Date',
        formatter: data =>
          formatDateValue(data.rcaValidatedDate || data.rcaApprovedDate)
      },
      {
        Header: 'Approve/Rejected Date',
        formatter: data => formatDateValue(data.approvedRejectedDate)
      }
    ];
  }

  if (role === claimRoleTypes.CLIENT_USER || role === claimRoleTypes.RP_USER) {
    return [
      {
        Header: 'CN',
        formatter: data => data.cnote || data.cnNumber || data.cn || ''
      },
      {
        Header: 'Reason',
        formatter: data => {
          const reasonId = data.reason || data.reasonId;
          const foundReason = reasonList.find(r => r && r.id === reasonId);
          return foundReason ? foundReason.reason : reasonId || '-';
        }
      },
      {
        Header: 'Status',
        formatter: data => {
          const val =
            data.displayStatus ||
            getClientVisibleStatus(data.status || '-', data.isRework || false);
          return val ? val.toUpperCase() : '-';
        }
      },
      {
        Header: 'Request amount',
        formatter: data =>
          data.requestedAmount !== null && data.requestedAmount !== undefined
            ? data.requestedAmount
            : '-'
      },
      {
        Header: 'Approved amount',
        formatter: data =>
          data.approvedAmount !== null && data.approvedAmount !== undefined
            ? data.approvedAmount
            : '-'
      },
      {
        Header: 'Claim Action',
        formatter: data => data.claimAction || '-'
      },
      {
        Header: 'Request raised Date',
        formatter: data =>
          formatDateValue(data.raisedDate || data.requestedDate)
      },
      {
        Header: 'Acknowledgement Date',
        formatter: data => formatDateValue(data.ackDate)
      },
      {
        Header: 'Closure Date',
        formatter: data => formatDateValue(data.closureDate)
      }
    ];
  }

  const columns = getColumnsForRole(role) || [];

  return columns.map(col => {
    let headerText = col.Header;
    if (typeof headerText === 'function') {
      headerText = col.accessor || 'Column';
    }

    const formatter = data => {
      const { accessor } = col;
      const rawValue = data[accessor];

      if (accessor === 'cnNumber') {
        return data.cnote || data.cnNumber || data.cn || '';
      }
      if (accessor === 'reason') {
        const reasonId = data.reason || data.reasonId;
        const foundReason = reasonList.find(r => r && r.id === reasonId);
        return foundReason ? foundReason.reason : reasonId || '-';
      }
      if (accessor === 'status') {
        const status = data.status || '-';
        if (status === '-') return '-';
        return status.replace(/_/g, ' ').toUpperCase();
      }
      if (accessor === 'sla') {
        if (data.sla) return data.sla;
        const remaining = data.slaRemainingMillis;
        if (remaining === undefined || remaining === null) return '-';
        const totalMinutes = Math.floor(Math.abs(remaining) / (1000 * 60));
        const days = Math.floor(totalMinutes / (60 * 24));
        const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
        const minutes = totalMinutes % 60;
        return `${days}D ${hours}H ${minutes}M`;
      }
      if (accessor === 'claimType') {
        return data.claimAction || 'COF';
      }
      if (accessor === 'clmRmAck') {
        const val = data.rawData ? data.rawData.clmRmAck : data.clmRmAck;
        return val ? 'Yes' : '-';
      }
      if (
        [
          'requestedDate',
          'ackDate',
          'closureDate',
          'lastUpdated',
          'rcaSubmittedDate',
          'rcaValidatedDate',
          'approvedRejectedDate'
        ].includes(accessor)
      ) {
        let timestampVal = rawValue;
        if (accessor === 'requestedDate' && data.raisedDate) {
          timestampVal = data.raisedDate;
        }
        return formatDateValue(timestampVal);
      }

      if (rawValue === undefined || rawValue === null) {
        return '';
      }
      return rawValue;
    };

    return {
      Header: headerText,
      formatter
    };
  });
};

export const downloadClaimsCsv = () => (dispatch, getState) => {
  const state = getState();
  const { claimsDashboardState } = state;
  const { userProfile } = state;
  const role = getClaimRole(userProfile);
  const isClientUser =
    role === claimRoleTypes.CLIENT_USER || role === claimRoleTypes.RP_USER;

  dispatch(
    setClaimsDataAction({
      isLoading: true
    })
  );

  const { claimFilterDTO, hasFilter } = buildClaimFilters(
    claimsDashboardState,
    isClientUser
  );

  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: 'getClaimsList',
    method: 'POST',
    replaceParamsArr: [
      { key: 'page', val: 0 },
      { key: 'size', val: claimsDashboardState.totalElements || 10000 }
    ],
    data: hasFilter ? claimFilterDTO : null,
    successCb: res => {
      dispatch(
        setClaimsDataAction({
          isLoading: false
        })
      );
      if (res.status === 'SUCCESS') {
        const payload = res.payload || {};
        const content = payload.content || [];
        const reasonList =
          (state.initServiceState && state.initServiceState.reasonList) || [];
        const csvColumns = getCSVColumnsForRole(role, reasonList);
        const csvData = createCSV(content, 'Claims_Report', csvColumns);
        downloadCSV(csvData, 'Claims_Report');
      } else if (res && res.status !== 'FAILURE') {
        toastr.error(
          'Something went wrong!',
          (res && res.errorMessage) || 'Could not download claims report'
        );
      }
    },
    failureCb: res => {
      dispatch(
        setClaimsDataAction({
          isLoading: false
        })
      );
      toastr.error(
        'Something went wrong!',
        (res && res.errorMessage) || 'Could not download claims report'
      );
    },
    catchCb: () => {
      dispatch(
        setClaimsDataAction({
          isLoading: false
        })
      );
      toastr.error('Something went wrong!', 'Could not download claims report');
    }
  };

  return makeApiCall(requestObj);
};
